'use client';
/**
 * LES QUANTITÉS DU STOCK (RQ, 20/10/2026).
 *
 * « Leur stock doit être suivi à minima mensuellement (inventaire) » — puis :
 * « on essaie de ne plus utiliser Boxystock et passer tout le stock sur
 * Nafis ». Cet écran tient le registre des ENTRÉES, SORTIES et COMPTAGES ; la
 * quantité de chaque produit s'en calcule (`backend/src/stock-quantites.ts`).
 *
 * ── Ce que l'écran montre en premier ────────────────────────────────────────
 * Les INDISPENSABLES : ce qui est à commander (seuil atteint) et ce dont le
 * comptage mensuel est dépassé. Les autres produits sont à un clic.
 *
 * ── Ce que l'écran refuse ──────────────────────────────────────────────────
 *  · ÉCRIRE UNE QUANTITÉ. On enregistre un geste (entrée, sortie, comptage) ;
 *    le nombre se calcule. Une erreur se corrige par le geste inverse.
 *  · AFFICHER « 0 » POUR UN PRODUIT JAMAIS COMPTÉ : c'est « inconnue ».
 *  · MONTRER LE STOCK ATTENDU PENDANT LA SAISIE DE L'INVENTAIRE : on compte à
 *    l'aveugle, l'écart se calcule à l'enregistrement.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I, card, inputStyle, FilterBar, FieldError, Modale, Pastille, Selecteur } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { Tableau } from '@/shared/Tableau';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { useGed } from '@/lib/store';
import { fmtDate, fmtDateTime } from '@/lib/meta';
import { moteur } from './manuel/Registres';
import { ListeIndispensables, FAMILLES, nombre, type LigneStock } from './ListeIndispensables';

type Mouvement = {
  documentId: string; produitCode: string; produitLibelle: string; type: 'entree' | 'sortie' | 'comptage';
  quantite: number; theorique: number | null; faitLe: string; lotRef: string; commentaire: string;
  missionRef: string; parLibelle: string; saisiLe: string;
};
type MissionIndisp = { documentId: string; ref: string | null; libelle: string; responsableLibelle: string | null; prochaineEcheance: string | null };
type Etat = {
  aujourdhui: string; lignes: LigneStock[]; mouvements: Mouvement[]; missions: MissionIndisp[];
  compte: { indispensables: number; comptageEnRetard: number; seuilAtteint: number; suivis: number };
};

const LIBELLE_TYPE: Record<Mouvement['type'], string> = { entree: 'Entrée', sortie: 'Sortie', comptage: 'Comptage' };
const TON_TYPE: Record<Mouvement['type'], 'success' | 'warning' | 'info'> = { entree: 'success', sortie: 'warning', comptage: 'info' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-body)', marginBottom: 4 };
const unite = (l: { uniteStock: string }) => l.uniteStock || 'unité(s)';

function Quantite({ l }: { l: LigneStock }) {
  if (l.quantite === null) return <em style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>inconnue</em>;
  return (
    <span style={{ fontSize: 13 }}>
      <strong style={{ color: 'var(--text-strong)' }}>{nombre(l.quantite)}</strong> {unite(l)}
      {!l.depuisComptage && <span style={{ display: 'block', fontSize: 11, color: 'var(--amber-700)' }} title="Calculée depuis zéro : aucun comptage ne l'a encore fixée">sans comptage</span>}
    </span>
  );
}

/** Une entrée ou une sortie, pour un produit. */
function FormMouvement({ ligne, type, aujourdhui, onFerme, onFait }: {
  ligne: LigneStock; type: 'entree' | 'sortie'; aujourdhui: string; onFerme: () => void; onFait: (m: string) => void;
}) {
  const [v, setV] = useState({ quantite: '', faitLe: aujourdhui, lotRef: '', commentaire: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = () => {
    setEnvoi(true); setErreur('');
    void moteur('stock-quantites/mouvements', { produitCode: ligne.code, type, ...v }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      onFait(`${type === 'entree' ? 'Entrée' : 'Sortie'} enregistrée — ${ligne.libelle}.`);
    });
  };
  return (
    <Modale titre={`${type === 'entree' ? 'Entrée en stock' : 'Sortie de stock'} — ${ligne.libelle}`}
      sousTitre={<>Au registre : {ligne.quantite === null ? 'quantité inconnue' : `${nombre(ligne.quantite)} ${unite(ligne)}`}</>}
      onClose={onFerme}
      pied={<PiedActions taille="sm" annuler={{ onClick: onFerme }}
        principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: envoi || !v.quantite, onClick: envoyer }} />}>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl} htmlFor="mv-q">Quantité ({unite(ligne)}) *</label>
          <input id="mv-q" type="number" min={0} step="any" value={v.quantite} onChange={(e) => setV({ ...v, quantite: e.target.value })} style={inputStyle} autoFocus /></div>
        <div><label style={lbl} htmlFor="mv-d">Date du geste</label>
          <input id="mv-d" type="date" max={aujourdhui} value={v.faitLe} onChange={(e) => setV({ ...v, faitLe: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="mv-l">Numéro de lot</label>
          <input id="mv-l" value={v.lotRef} onChange={(e) => setV({ ...v, lotRef: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="mv-c">{type === 'sortie' ? 'Motif (consommation, rebut, prêt…)' : 'Commentaire'}</label>
          <input id="mv-c" value={v.commentaire} onChange={(e) => setV({ ...v, commentaire: e.target.value })} style={inputStyle} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 0' }}>
        Un mouvement ne se modifie pas : une erreur se corrige par le mouvement inverse, et le registre garde les deux.
      </p>
    </Modale>
  );
}

/** L'INVENTAIRE : un comptage par produit, à l'aveugle. */
function FormInventaire({ etat, onFerme, onFait }: { etat: Etat; onFerme: () => void; onFait: (m: string) => void }) {
  const [tous, setTous] = useState(false);
  const [faitLe, setFaitLe] = useState(etat.aujourdhui);
  const [missionId, setMissionId] = useState(etat.missions.length === 1 ? etat.missions[0]!.documentId : '');
  const [saisies, setSaisies] = useState<Record<string, { quantite: string; commentaire: string }>>({});
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const lignes = useMemo(() => etat.lignes.filter((l) => tous || l.indispensable), [etat.lignes, tous]);
  const comptes = Object.entries(saisies).filter(([, s]) => s.quantite.trim() !== '');
  const indispManquants = missionId ? etat.lignes.filter((l) => l.indispensable && !(saisies[l.code]?.quantite.trim())).length : 0;
  const maj = (code: string, champ: 'quantite' | 'commentaire', val: string) =>
    setSaisies((s) => ({ ...s, [code]: { quantite: s[code]?.quantite ?? '', commentaire: s[code]?.commentaire ?? '', [champ]: val } }));
  const envoyer = () => {
    setEnvoi(true); setErreur('');
    void moteur('stock-quantites/inventaire', {
      faitLe, missionId: missionId || undefined, commentaire,
      lignes: comptes.map(([produitCode, s]) => ({ produitCode, quantite: s.quantite, commentaire: s.commentaire })),
    }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      const ecarts = (r.data.ecarts as string[]) || [];
      onFait(`Inventaire enregistré — ${r.data.comptes} produit(s) compté(s), ${ecarts.length ? `${ecarts.length} écart(s) au registre` : 'aucun écart au registre'}.`);
    });
  };
  return (
    <Modale titre="Saisir l’inventaire" largeur={980} onClose={onFerme}
      sousTitre="Saisissez ce que vous avez compté. Le stock attendu n’est pas affiché : on compte à l’aveugle, l’écart se calcule à l’enregistrement."
      pied={<PiedActions taille="sm" annuler={{ onClick: onFerme }}
        principale={{ libelle: `Enregistrer ${comptes.length} comptage(s)`, libelleChargement: 'Enregistrement…', chargement: envoi,
          disabled: envoi || !comptes.length || indispManquants > 0, onClick: envoyer,
          title: indispManquants > 0 ? `${indispManquants} indispensable(s) non compté(s) — la mission exige l’inventaire complet` : undefined }} />}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px 16px' }}>
          <div><label style={lbl} htmlFor="inv-d">Date du comptage</label>
            <input id="inv-d" type="date" max={etat.aujourdhui} value={faitLe} onChange={(e) => setFaitLe(e.target.value)} style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="inv-m">Au titre de la mission d’inventaire</label>
            <Selecteur id="inv-m" value={missionId} onChange={setMissionId} style={inputStyle}>
              <option value="">— aucune (comptage libre) —</option>
              {etat.missions.map((m) => <option key={m.documentId} value={m.documentId}>{m.ref ? `${m.ref} · ` : ''}{m.libelle}{m.prochaineEcheance ? ` — échéance ${fmtDate(m.prochaineEcheance)}` : ''}</option>)}
            </Selecteur>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
              {missionId
                ? <>La mission sera <strong>accusée</strong> avec le nombre d’écarts. Tous les indispensables doivent être comptés{indispManquants > 0 ? <> — il en manque <strong>{indispManquants}</strong></> : ''}.</>
                : etat.missions.length ? 'Choisir une mission l’accuse dans le même geste.' : 'Aucune mission « indispensables » n’est planifiée (Prêts & distribution › Missions d’inventaire).'}
            </div></div>
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={tous} onChange={(e) => setTous(e.target.checked)} />
          Afficher aussi les produits non indispensables
        </label>
        <Tableau
          id="stock-inventaire-saisie"
          ajusterLargeur
          niveau="standard"
          parPage={1000}
          lignes={lignes}
          cleLigne={(l) => l.code}
          vide={{ titre: 'Aucun produit indispensable.', indice: 'Cochez « Indispensable » au catalogue des produits, ou affichez tous les produits.' }}
          colonnes={[
            { cle: 'libelle', libelle: 'Produit', valeur: (l) => l.libelle, epinglee: true, obligatoire: true,
              rendu: (l) => <span><strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>{l.libelle}</strong>{l.conditionnement && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{l.conditionnement}</span>}</span> },
            { cle: 'famille', libelle: 'Famille', type: 'liste', valeur: (l) => l.famille, libelleValeur: (v) => FAMILLES[v] || v, rendu: (l) => <span style={{ fontSize: 12.5 }}>{FAMILLES[l.famille] || l.famille}</span> },
            { cle: 'compte', libelle: 'Compté', obligatoire: true,
              rendu: (l) => (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <input type="number" min={0} step="any" aria-label={`Quantité comptée — ${l.libelle}`} value={saisies[l.code]?.quantite ?? ''}
                    onChange={(e) => maj(l.code, 'quantite', e.target.value)} style={{ ...inputStyle, width: 90 }} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{unite(l)}</span>
                </span>
              ) },
            { cle: 'commentaire', libelle: 'Commentaire',
              rendu: (l) => <input aria-label={`Commentaire — ${l.libelle}`} value={saisies[l.code]?.commentaire ?? ''} onChange={(e) => maj(l.code, 'commentaire', e.target.value)} style={{ ...inputStyle, minWidth: 160 }} /> },
          ]}
        />
        <div><label style={lbl} htmlFor="inv-c">Commentaire général</label>
          <input id="inv-c" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} style={inputStyle} /></div>
        <FieldError>{erreur}</FieldError>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
          Une case vide = produit non compté (rien n’est enregistré pour lui). Zéro = rupture constatée. Le comptage fait foi : il remet le registre au nombre constaté et garde l’écart.
        </p>
      </div>
    </Modale>
  );
}

type Vue = 'indispensables' | 'commander' | 'retard' | 'tous';

export function StockQuantites() {
  const { showToast } = useGed();
  const [etat, setEtat] = useState<Etat | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [tour, setTour] = useState(0);
  const [vue, setVue] = useState<Vue>('indispensables');
  const [q, setQ] = useState('');
  const [mouvement, setMouvement] = useState<{ ligne: LigneStock; type: 'entree' | 'sortie' } | null>(null);
  const [inventaire, setInventaire] = useState(false);
  const [impression, setImpression] = useState(false);
  useEffect(() => {
    let vivant = true;
    setChargement(true);
    void moteur('stock-quantites').then((r) => {
      if (!vivant) return;
      setChargement(false);
      if (!r.ok) { setErreur(r.message || 'Les quantités n’ont pas pu être chargées.'); return; }
      setErreur(''); setEtat(r.data as unknown as Etat);
    });
    return () => { vivant = false; };
  }, [tour]);
  const fait = useCallback((m: string) => { setMouvement(null); setInventaire(false); showToast(m); setTour((t) => t + 1); }, [showToast]);

  const lignes = useMemo(() => {
    const base = (etat?.lignes || []).filter((l) => (vue === 'tous' ? true
      : vue === 'commander' ? l.seuilAtteint
        : vue === 'retard' ? l.comptageEnRetard : l.indispensable));
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((l) => `${l.libelle} ${l.code} ${l.fabricant} ${l.fournisseurNom} ${l.refFournisseur}`.toLowerCase().includes(qq)) : base;
  }, [etat, vue, q]);
  const c = etat?.compte;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '80ch', flex: 1, minWidth: 260 }}>
          La quantité de chaque produit se <strong>calcule</strong> depuis le registre : le dernier comptage, plus les entrées, moins les sorties.
          Les <strong>indispensables</strong> (cochés au catalogue des produits) se comptent au moins une fois par mois.
        </p>
        <BoutonJournal reference="REG-STOCK-QUANTITES" quoi="des mouvements de stock" />
        <Bouton onClick={() => setImpression(true)}><I n="print" s={14} />Liste des indispensables</Bouton>
        <Bouton variante="primaire" disabled={!etat} onClick={() => setInventaire(true)}><I n="check" s={14} />Saisir l’inventaire</Bouton>
      </div>
      {erreur && <div role="alert" style={{ ...card, padding: 12, borderInlineStart: '3px solid var(--red-600)', fontSize: 12.5 }}>{erreur} Ce n’est <strong>pas</strong> « aucun stock ».</div>}
      <FilterBar q={q} onQ={setQ} placeholder="Rechercher un produit, un fabricant, une référence…"
        chips={[
          { k: 'indispensables', label: 'Indispensables', n: c?.indispensables ?? 0 },
          { k: 'commander', label: 'À commander', n: c?.seuilAtteint ?? 0 },
          { k: 'retard', label: 'Comptage en retard', n: c?.comptageEnRetard ?? 0 },
          { k: 'tous', label: 'Tous les produits', n: etat?.lignes.length ?? 0 },
        ]}
        actif={vue} onChip={(k) => setVue(k as Vue)} total={etat?.lignes.length ?? 0} affiches={lignes.length}
        onReset={() => { setQ(''); setVue('indispensables'); }} />
      <Tableau
        id="stock-quantites"
        ajusterLargeur
        niveau="standard"
        chargement={chargement && !etat}
        lignes={lignes}
        cleLigne={(l) => l.code}
        vide={{
          titre: vue === 'indispensables' && !(c?.indispensables) ? 'Aucun produit n’est marqué indispensable.' : 'Aucun produit ne correspond.',
          indice: vue === 'indispensables' ? 'Cochez « Indispensable » dans la fiche des produits concernés, au catalogue des produits (Matériel).' : undefined,
        }}
        colonnes={[
          { cle: 'libelle', libelle: 'Produit', valeur: (l) => l.libelle, epinglee: true, obligatoire: true,
            rendu: (l) => <span><strong style={{ display: 'block', fontSize: 13, color: 'var(--text-strong)' }}>{l.libelle}</strong><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{[l.fabricant, l.conditionnement].filter(Boolean).join(' · ')}</span></span> },
          { cle: 'famille', libelle: 'Famille', type: 'liste', valeur: (l) => l.famille, libelleValeur: (v) => FAMILLES[v] || v, rendu: (l) => <span style={{ fontSize: 12.5 }}>{FAMILLES[l.famille] || l.famille}</span> },
          { cle: 'indispensable', libelle: 'Indispensable', type: 'liste', valeur: (l) => (l.indispensable ? 'oui' : 'non'), libelleValeur: (v) => (v === 'oui' ? 'Indispensable' : 'Non'),
            exporter: (l) => (l.indispensable ? 'oui' : ''), rendu: (l) => (l.indispensable ? <Pastille ton="danger" compacte>Indispensable</Pastille> : null) },
          { cle: 'quantite', libelle: 'En stock', type: 'nombre', valeur: (l) => l.quantite, exporter: (l) => (l.quantite === null ? '' : String(l.quantite)), rendu: (l) => <Quantite l={l} /> },
          { cle: 'minimum', libelle: 'Commander à', type: 'nombre', valeur: (l) => l.stockMinimum, rendu: (l) => <span style={{ fontSize: 12.5 }}>{l.stockMinimum === null ? <em style={{ color: 'var(--text-muted)' }}>—</em> : `${nombre(l.stockMinimum)} ${unite(l)}`}</span> },
          { cle: 'etat', libelle: 'État', valeur: (l) => (l.seuilAtteint ? 'À commander' : l.quantite === null ? 'Inconnu' : 'Suffisant'),
            rendu: (l) => (l.seuilAtteint ? <Pastille ton="warning" compacte>À commander</Pastille>
              : l.quantite === null ? <Pastille ton="muted" compacte>Inconnu</Pastille>
                : l.stockMinimum === null ? null : <Pastille ton="success" compacte>Suffisant</Pastille>) },
          { cle: 'comptage', libelle: 'Dernier comptage', type: 'date', valeur: (l) => l.dernierComptage,
            rendu: (l) => (
              <span style={{ fontSize: 12.5 }}>
                {l.dernierComptage ? fmtDate(l.dernierComptage) : <em style={{ color: 'var(--text-muted)' }}>jamais</em>}
                {l.comptageEnRetard && <span style={{ display: 'block' }}><Pastille ton="danger" compacte>Comptage dû{l.prochainComptage && l.dernierComptage ? ` depuis le ${fmtDate(l.prochainComptage)}` : ''}</Pastille></span>}
                {!l.comptageEnRetard && l.prochainComptage && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>prochain avant le {fmtDate(l.prochainComptage)}</span>}
              </span>
            ) },
        ]}
        actions={(l) => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <Bouton taille="sm" aria-label={`Entrée — ${l.libelle}`} onClick={() => setMouvement({ ligne: l, type: 'entree' })}><I n="plus" s={12} />Entrée</Bouton>
            <Bouton taille="sm" aria-label={`Sortie — ${l.libelle}`} onClick={() => setMouvement({ ligne: l, type: 'sortie' })}>Sortie</Bouton>
          </span>
        )}
      />

      <h3 style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>Derniers mouvements</h3>
      <Tableau
        id="stock-mouvements"
        ajusterLargeur
        niveau="standard"
        parPage={25}
        chargement={chargement && !etat}
        lignes={etat?.mouvements || []}
        cleLigne={(m) => m.documentId}
        vide={{ titre: 'Aucun mouvement enregistré.', indice: 'Commencez par un inventaire : il fixe le stock de départ de chaque produit compté.' }}
        colonnes={[
          { cle: 'faitLe', libelle: 'Date', type: 'date', valeur: (m) => m.faitLe, rendu: (m) => <span style={{ fontSize: 12.5 }}>{fmtDate(m.faitLe)}</span> },
          { cle: 'produit', libelle: 'Produit', valeur: (m) => m.produitLibelle, rendu: (m) => <span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.produitLibelle}</span> },
          { cle: 'type', libelle: 'Geste', type: 'liste', valeur: (m) => m.type, libelleValeur: (v) => LIBELLE_TYPE[v as Mouvement['type']] || v,
            rendu: (m) => <Pastille ton={TON_TYPE[m.type]} compacte>{LIBELLE_TYPE[m.type]}</Pastille> },
          { cle: 'quantite', libelle: 'Quantité', type: 'nombre', valeur: (m) => m.quantite,
            rendu: (m) => <span style={{ fontSize: 12.5 }}>{m.type === 'sortie' ? '−' : m.type === 'entree' ? '+' : ''}{nombre(m.quantite)}</span> },
          { cle: 'ecart', libelle: 'Écart au registre', valeur: (m) => (m.type === 'comptage' && m.theorique !== null ? m.quantite - m.theorique : null),
            rendu: (m) => {
              if (m.type !== 'comptage') return null;
              if (m.theorique === null) return <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>premier comptage</span>;
              const e = Math.round((m.quantite - m.theorique) * 1000) / 1000;
              return e === 0 ? <span style={{ fontSize: 12, color: 'var(--green-600)' }}>conforme</span>
                : <span style={{ fontSize: 12, color: 'var(--amber-700)' }}>{e > 0 ? '+' : '−'}{nombre(Math.abs(e))} (attendu {nombre(m.theorique)})</span>;
            } },
          { cle: 'par', libelle: 'Par', valeur: (m) => m.parLibelle, rendu: (m) => <span style={{ fontSize: 12.5 }}>{m.parLibelle}<span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{fmtDateTime(m.saisiLe)}</span></span> },
          { cle: 'detail', libelle: 'Lot · motif · mission', valeur: (m) => [m.lotRef, m.commentaire, m.missionRef].filter(Boolean).join(' · '),
            rendu: (m) => <span style={{ fontSize: 12 }}>{[m.lotRef && `lot ${m.lotRef}`, m.commentaire, m.missionRef].filter(Boolean).join(' · ')}</span> },
        ]}
      />

      {mouvement && etat && <FormMouvement ligne={mouvement.ligne} type={mouvement.type} aujourdhui={etat.aujourdhui} onFerme={() => setMouvement(null)} onFait={fait} />}
      {inventaire && etat && <FormInventaire etat={etat} onFerme={() => setInventaire(false)} onFait={fait} />}
      {impression && <ListeIndispensables lignes={etat?.lignes} onClose={() => setImpression(false)} />}
    </div>
  );
}
